import { canPerformAction } from '../../../utils/permissions';
import type { ConsultationPrescription, MedicalConsultation } from '../../../types/api';
import type { AuthUser } from '../../../services/authService';
import type { ClinicalAmendment } from '../../../services/clinicalAmendmentService';
import type { MedicalReferral } from '../../../services/medicalReferralService';

/**
 * Client-side mirrors of ClinicalAuthorshipService's correction checks
 * (clinical-record-amendments R3-R5, R8), used only to decide which actions
 * to render. The backend stays the authority: these never grant anything the
 * API would refuse, and a 403/422 from the API is still surfaced as-is.
 */

type CorrectionUser = Pick<AuthUser, 'id' | 'doctor' | 'is_admin' | 'profile'> | null | undefined;

const SIGNED_STATES = ['signed', 'amended'];

const isDoctorOf = (user: CorrectionUser, doctorId: number) => !!user?.doctor && user.doctor.id === doctorId;
const hasSignAction = (user: CorrectionUser) => !!user && canPerformAction(user, 'consultations', 'sign');

/**
 * assertCorrectionAuthority: the responsible doctor always qualifies; an
 * assistant only on a record they themself signed by delegation AND while
 * the given doctor ids allow it: stored consent (`can_sign`) for amendment
 * drafting, delegated signing in effect (`can_sign_delegated`) for void +
 * re-issue. No admin bypass — the backend has none.
 */
export function hasCorrectionAuthority(
  user: CorrectionUser,
  doctorId: number,
  signatureMethod: string | null | undefined,
  signedBy: number | null | undefined,
  authorizedDoctorIds: number[],
): boolean {
  if (!user) return false;
  if (isDoctorOf(user, doctorId)) return true;
  return signatureMethod === 'delegated' && signedBy === user.id && authorizedDoctorIds.includes(doctorId);
}

/** assertCanCorrect + the `consultations,create` gate on the amendments route. */
export function canAmendConsultation(
  consultation: MedicalConsultation | null | undefined,
  user: CorrectionUser,
  consentedDoctorIds: number[],
): boolean {
  if (!consultation || !user || !SIGNED_STATES.includes(consultation.status ?? '')) return false;
  if (!canPerformAction(user, 'consultations', 'create')) return false;
  return hasCorrectionAuthority(user, consultation.doctor_id, consultation.signature_method, consultation.signed_by, consentedDoctorIds);
}

/** Doctor-only in this phase, same posture as useCanSign (delegated signing is Phase 5). */
export function canSignAmendment(amendment: ClinicalAmendment, user: CorrectionUser): boolean {
  return amendment.status === 'draft'
    && isDoctorOf(user, amendment.doctor_id)
    && hasSignAction(user);
}

/**
 * assertCanVoid: void + re-issue is itself a signing act, so it also needs
 * `consultations,sign`, and an assistant needs delegated signing to be in
 * effect right now (`signerDoctorIds`, my-doctors `can_sign_delegated`: the
 * backend's signingAct gate), not just stored consent. The relevant
 * signature is the prescription's own when it is a re-issue, otherwise its
 * consultation's (R3).
 */
export function canVoidPrescription(
  prescription: ConsultationPrescription,
  consultation: MedicalConsultation | null | undefined,
  user: CorrectionUser,
  signerDoctorIds: number[],
): boolean {
  if (!consultation || prescription.voided_at || !SIGNED_STATES.includes(consultation.status ?? '')) return false;
  if (!hasSignAction(user)) return false;
  const sig = prescription.signature_method ? prescription : consultation;
  return hasCorrectionAuthority(user, consultation.doctor_id, sig.signature_method, sig.signed_by, signerDoctorIds);
}

/** Voided prescriptions stay in history but are never printed (R5). */
export function activePrescriptions(prescriptions: ConsultationPrescription[] | null | undefined): ConsultationPrescription[] {
  return (prescriptions ?? []).filter((p) => !p.voided_at);
}

/**
 * Referrals (R7, R8). A `locked_with_consultation` referral follows its
 * consultation: no sign action of its own, correctable once the parent is
 * signed, under the parent's signature. A standalone referral (added after
 * the consultation was signed) is signed on its own and corrected under
 * its own signature. Authority is always anchored on the consultation's
 * doctor, as in the backend.
 */
type ReferralParent = Pick<MedicalConsultation, 'status' | 'doctor_id' | 'signature_method' | 'signed_by'> | null | undefined;

/** assertCanSignReferral — doctor-only in this phase, like canSignAmendment. */
export function canSignReferral(referral: MedicalReferral, consultation: ReferralParent, user: CorrectionUser): boolean {
  return !!consultation
    && !referral.locked_with_consultation
    && referral.signing_status === 'draft'
    && !referral.voided_at
    && isDoctorOf(user, consultation.doctor_id)
    && hasSignAction(user);
}

/**
 * assertCanVoidReferral + the `consultations,sign` gate on the reissue route.
 * A signing act, so `signerDoctorIds` is the in-effect set, as for prescriptions.
 */
export function canVoidReferral(
  referral: MedicalReferral,
  consultation: ReferralParent,
  user: CorrectionUser,
  signerDoctorIds: number[],
): boolean {
  if (!consultation || referral.voided_at || !hasSignAction(user)) return false;
  const signedYet = referral.locked_with_consultation
    ? SIGNED_STATES.includes(consultation.status ?? '')
    : referral.signing_status === 'signed';
  if (!signedYet) return false;
  const sig = referral.locked_with_consultation ? consultation : referral;
  return hasCorrectionAuthority(user, consultation.doctor_id, sig.signature_method, sig.signed_by, signerDoctorIds);
}

/**
 * forbidUnlessActingDoctor on store: the doctor, or an assistant who can act
 * for them right now (`actingDoctorIds`, my-doctors `can_act`). While the consultation is a draft the add
 * button keeps its pre-existing visibility; after signing, the new referral
 * is born standalone (4.7e).
 */
export function canAddReferral(consultation: ReferralParent, user: CorrectionUser, actingDoctorIds: number[]): boolean {
  if (!consultation) return false;
  if (!SIGNED_STATES.includes(consultation.status ?? '')) return true;
  if (!user) return false;
  return isDoctorOf(user, consultation.doctor_id) || (!user.doctor && actingDoctorIds.includes(consultation.doctor_id));
}

/** Only unsigned drafts can be deleted: the model guards reject the rest. */
export function canDeleteReferral(
  referral: MedicalReferral,
  consultation: ReferralParent,
  user: CorrectionUser,
  actingDoctorIds: number[],
): boolean {
  if (!consultation || referral.voided_at) return false;
  const isDraft = referral.locked_with_consultation
    ? consultation.status === 'draft'
    : referral.signing_status === 'draft';
  return isDraft && canAddReferral(consultation, user, actingDoctorIds);
}
